import { yupResolver } from '@hookform/resolvers/yup';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { Form, Input } from 'components/elements';
import AfterTodayDatePicker from 'components/elements/after-today-date-picker';
import { Button } from 'components/elements/buttons';
import { FullContainer, SeparatorHorizontal } from 'components/styles';
import { differenceInDays } from 'date-fns';
import useDate from 'hooks/use-date';
import { DrawerProvider } from 'hooks/use-drawer';
import { FormContent } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import { ReminderFormSchema, ReminderFormType } from './form-type';

interface ReminderDrawerProps {
  onClose: () => void;
  parentIndex: number;
  parentMethods: any;
}

const name = 'documentSales' as const;

export default function ReminderDrawer(props: ReminderDrawerProps) {
  const { t } = useTranslation();
  const formatDate = useDate();
  const { onClose, parentIndex, parentMethods } = props;
  const { getValues, setValue, clearErrors } = parentMethods;

  const initialValues = React.useMemo(() => {
    return {
      reminderDate: getValues(`${name}.${parentIndex}.reminderDate`),
      reminderDescription: getValues(
        `${name}.${parentIndex}.reminderDescription`,
      ),
    };
  }, [getValues, parentIndex]);

  const defaultValues = React.useMemo<ReminderFormType>(() => {
    return (
      initialValues ?? {
        reminderDate: null,
        reminderDescription: '',
      }
    );
  }, [initialValues]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(ReminderFormSchema()),
  });

  const onSubmitReminder = React.useCallback(
    (values: ReminderFormType) => {
      if (values.reminderDate) {
        setValue(`${name}.${parentIndex}.reminder`, true);
        setValue(`${name}.${parentIndex}.reminderDate`, values.reminderDate, {
          shouldValidate: true,
        });
        setValue(
          `${name}.${parentIndex}.reminderDescription`,
          values.reminderDescription,
          {
            shouldValidate: true,
          },
        );
        clearErrors();
        onClose();

        const reminderDate = new Date(
          getValues(`${name}.${parentIndex}.reminderDate`),
        );
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (differenceInDays(reminderDate, today) <= 0) {
          notification.warning({
            message: t('ticketSales:reminder_date_warning'),
          });
        } else {
          const reminderFormattedDate = formatDate(reminderDate);
          notification.info({
            message: `${t(
              'ticketSales:reminder_set_on',
            )} ${reminderFormattedDate}`,
          });
        }
      } else {
        setValue(`${name}.${parentIndex}.reminder`, false);
        setValue(`${name}.${parentIndex}.reminderDate`, null);
        setValue(`${name}.${parentIndex}.reminderDescription`, '');
        clearErrors();
        onClose();
      }
    },
    [clearErrors, formatDate, getValues, onClose, parentIndex, setValue, t],
  );

  return (
    <DrawerProvider>
      <DrawerHeader onClose={onClose} title={t('ticketSales:set_reminder')} />
      <DrawerBody>
        <Form methods={methods} onSubmit={onSubmitReminder}>
          <FormContent style={{ overflow: 'hidden' }}>
            <AfterTodayDatePicker
              name="reminderDate"
              label={t('common:date')}
              placeholder="DD/MM/YYYY"
            />
            <Input
              type="text-area"
              name="reminderDescription"
              label={t('common:description')}
            />
            <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
              <Button buttonType="tertiary" onClick={onClose}>
                {t('common:cancel')}
              </Button>
              <SeparatorHorizontal $margin={8} />
              <Input type="submit" />
            </FullContainer>
          </FormContent>
        </Form>
      </DrawerBody>
    </DrawerProvider>
  );
}
